<script lang="ts">
  /**
   * Overlapping faces for "who did this", with a +N chip when the list runs long.
   *
   * WHY A COMPONENT AND NOT A FLEX ROW OF `<Avatar>`. The overlap is the whole point —
   * it says "these people, as a group" in the width of two avatars, which is what lets a
   * count sit next to a face instead of replacing it. Doing that inline needs a negative
   * margin on every child but the first, a stacking order that puts the FIRST face on top
   * (so the row reads left-to-right rather than as a fan), and a ring in the surface
   * colour to keep the faces separable. Three details, wrong in a different way each time
   * somebody rebuilds it.
   *
   * REDACTION COMES FOR FREE, and that is load-bearing. `<Avatar>` carries `.redact` on
   * the face and on its initial-letter fallback, so a page that shows who asked something
   * is private-mode-safe by rendering it the normal way. The `+N` chip carries no name
   * and needs no mark.
   *
   * Presentational: it takes people and renders them. It does not fetch, link or hover —
   * wrap a face in `<MemberHover>` at the call site when the page wants that.
   */
  import Avatar from './Avatar.svelte';

  export interface StackPerson {
    member_id?: number | null;
    name?: string | null;
    picture_profile?: string | null;
  }

  let {
    people = [],
    max = 5,
    size = 24,
    total = null,
    /** Surface the ring is drawn in, so faces separate against a tinted card too. */
    ring = 'ring-base-100',
    class: cls = '',
  }: {
    people?: StackPerson[];
    max?: number;
    size?: number;
    /** How many people there really are, when `people` is only a sample of them.
     *  The `+N` counts up to this instead of to `people.length`, so a row that shows
     *  three faces beside "nine asked" is not quietly claiming there were three. */
    total?: number | null;
    ring?: string;
    class?: string;
  } = $props();

  // Deduplicated by member: the same person asking three times is one face, which is the
  // difference between "how many people" and "how many questions" — a distinction this
  // component exists to make visible.
  const unique = $derived.by(() => {
    const seen = new Set<number | string>();
    const out: StackPerson[] = [];
    for (const p of people) {
      const key = p.member_id ?? p.name ?? '';
      if (key === '' || seen.has(key)) continue;
      seen.add(key);
      out.push(p);
    }
    return out;
  });
  const shown = $derived(unique.slice(0, max));
  const extra = $derived(Math.max(0, (total ?? unique.length) - shown.length));
</script>

<div class="flex items-center {cls}">
  {#each shown as person, i (person.member_id ?? person.name ?? i)}
    <!-- The FIRST face sits on top: the row should read as a line of people starting
         at the left, not as a fan opening to the right. -->
    <span
      class="rounded-full ring-2 {ring}"
      style="margin-left: {i === 0 ? 0 : -Math.round(size * 0.3)}px; z-index: {shown.length - i}"
    >
      <Avatar src={person.picture_profile ?? null} name={person.name ?? ''} {size} />
    </span>
  {/each}
  {#if extra > 0}
    <span
      class="eyebrow flex items-center justify-center rounded-full bg-base-300 ring-2 {ring} text-base-content/70"
      style="margin-left: {-Math.round(size * 0.3)}px; width: {size}px; height: {size}px; font-size: {Math.round(size * 0.38)}px; letter-spacing: 0"
    >+{extra}</span>
  {/if}
</div>
